/**
 * Approval pending (G7) — polls the entry every 3s while the resident's
 * phone buzzes. APPROVED → Allow entry (check-in). REJECTED → hard stop.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Image, View } from 'react-native';

import { space } from '@bero/tokens';
import { str } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Screen, TopBar } from '@/ui';

export default function GuardWaiting() {
  const { colors } = useTheme();
  const { t } = useT();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [entry, setEntry] = React.useState<Record<string, unknown> | null>(null);
  const [seconds, setSeconds] = React.useState(0);
  const [admitting, setAdmitting] = React.useState(false);

  React.useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const body = await api.get<{ entry: Record<string, unknown> }>(`/api/gate/entries/${encodeURIComponent(id)}`);
        if (!cancelled) setEntry(body.entry);
      } catch { /* transient */ }
    };
    void Promise.resolve().then(poll);
    const p = setInterval(poll, 3000);
    const s = setInterval(() => setSeconds((n) => n + 1), 1000);
    return () => { cancelled = true; clearInterval(p); clearInterval(s); };
  }, [id]);

  const status = ((entry?.status as string) ?? 'PENDING').toUpperCase();
  const name = str(entry, 'name') ?? 'Visitor';
  const photo = str(entry, 'photo');

  const admit = async () => {
    if (admitting) return;
    setAdmitting(true);
    try {
      await api.post<unknown>(`/api/gate/entries/${encodeURIComponent(String(id))}/checkin`, {});
      router.back();
    } catch { /* stays on screen */ } finally {
      setAdmitting(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('waiting_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s6, alignItems: 'center' }}>
        {photo ? (
          <Image source={{ uri: photo }} style={{ width: 112, height: 112, borderRadius: 56, backgroundColor: colors.canvas2 }} />
        ) : (
          <View style={{
            width: 112, height: 112, borderRadius: 56, backgroundColor: colors.canvas1,
            borderWidth: 2, borderColor: colors.borderSoft, alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="user" size={44} color={colors.textTertiary} />
          </View>
        )}
        <AppText variant="headingMd" weight="600" style={{ marginTop: space.s4 }}>{name}</AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 2 }}>
          {(str(entry, 'type') ?? 'visitor').toLowerCase()}
        </AppText>

        {status === 'PENDING' ? (
          <View style={{ alignItems: 'center', marginTop: space.s6 }}>
            <View style={{ flexDirection: 'row', gap: 6 }}>
              {[0.35, 0.6, 1].map((o, i) => (
                <View key={i} style={{ width: 9, height: 9, borderRadius: 5, backgroundColor: colors.warning500, opacity: o }} />
              ))}
            </View>
            <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s4 }}>
              {t('waiting_buzzing')}
            </AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 4 }}>
              {t('waiting_sent_ago', { n: seconds })}
            </AppText>
          </View>
        ) : status === 'APPROVED' ? (
          <View style={{ alignItems: 'center', marginTop: space.s6, alignSelf: 'stretch' }}>
            <Icon name="check-circle" size={40} color={colors.success500} />
            <AppText variant="bodyMd" weight="600" style={{ marginTop: space.s3, color: colors.success700 }}>
              {t('waiting_approved')}
            </AppText>
            <Button title={t('allow_entry')} size="lg" loading={admitting} onPress={() => void admit()} style={{ marginTop: space.s4, alignSelf: 'stretch' }} />
          </View>
        ) : status === 'REJECTED' ? (
          <View style={{ alignItems: 'center', marginTop: space.s6, alignSelf: 'stretch' }}>
            <Icon name="x" size={40} color={colors.danger500} />
            <AppText variant="bodyMd" weight="600" style={{ marginTop: space.s3, color: colors.danger500 }}>
              {t('waiting_rejected')}
            </AppText>
            <Button title={t('back_to_gate')} kind="secondary" onPress={() => router.back()} style={{ marginTop: space.s4, alignSelf: 'stretch' }} />
          </View>
        ) : (
          <View style={{ alignItems: 'center', marginTop: space.s6, alignSelf: 'stretch' }}>
            <AppText variant="bodyMd" weight="600" style={{ marginTop: space.s3 }}>
              {status === 'LEFT_AT_GATE' ? t('waiting_left_at_gate') : status}
            </AppText>
            <Button title={t('back_to_gate')} kind="secondary" onPress={() => router.back()} style={{ marginTop: space.s4, alignSelf: 'stretch' }} />
          </View>
        )}
      </Screen>
    </Screen>
  );
}
